import { useEffect, useMemo, useRef, useState } from "react";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb.jsx";
import {
  fetchQuestPackListAPI,
  fetchQuestPackPurchaseHistoryAPI,
  fetchQuestPackPurchaseStatsAPI,
} from "../../apis/questPack.api.js";
import {
  fetchHuntPackListAPI,
  fetchHuntPackPurchaseHistoryAPI,
  fetchHuntPackPurchaseStatsAPI,
} from "../../apis/huntPack.api.js";
import {
  fetchItemGroupListAPI,
  fetchItemPackPurchaseHistoryAPI,
  fetchItemPackPurchaseStatsAPI,
} from "../../apis/item.api.js";

const PACK_TYPE_OPTIONS = [
  { value: "quest", label: "Quest Packs" },
  { value: "item", label: "Challenge Packs" },
  { value: "hunt", label: "Hunt Packs" },
];

const LIMIT_OPTIONS = [10, 20, 50, 100];
const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "week", label: "Last Week" },
  { value: "month", label: "Last Month" },
  { value: "year", label: "Last Year" },
  { value: "day", label: "Day" },
  { value: "date_range", label: "Date range" },
];

const formatCurrency = (value) => {
  return Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-US");

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatAmount = (value) => {
  if (value === null || value === undefined || Number(value) === 0) {
    return <span className="badge bg-success">Free</span>;
  }
  return formatCurrency(value);
};

const buildDashboardStyleDateFilterPayload = (filters) => {
  const payload = {};

  if (!filters?.filter || filters.filter === "all") {
    return payload;
  }

  payload.filter = filters.filter;

  if (filters.filter === "day" && filters.day) {
    payload.day = filters.day;
  }

  if (filters.filter === "date_range") {
    if (filters.start_date) {
      payload.start_date = filters.start_date;
    }
    if (filters.end_date) {
      payload.end_date = filters.end_date;
    }
  }

  return payload;
};

const PurchaseFiltersCard = ({
  packLabel,
  packSelect,
  filters,
  onFilterChange,
  setFilters,
}) => (
  <div className="card border-0 shadow-sm mb-4">
    <div className="card-body">
      <div className="row g-3 align-items-end">
        <div className="col-12 col-lg-5">
          <label className="form-label fw-semibold">{packLabel}</label>
          {packSelect}
        </div>

        <div className="col-12 col-lg-3">
          <label className="form-label fw-semibold">Filter by Date</label>
          <select
            className="form-select"
            value={filters.filter}
            onChange={onFilterChange}
          >
            {FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {filters.filter === "day" ? (
          <div className="col-12 col-lg-4">
            <label className="form-label fw-semibold">Select Day</label>
            <input
              type="date"
              className="form-control"
              value={filters.day}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, day: event.target.value }))
              }
            />
          </div>
        ) : null}

        {filters.filter === "date_range" ? (
          <>
            <div className="col-12 col-lg-2">
              <label className="form-label fw-semibold">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    start_date: event.target.value,
                  }))
                }
              />
            </div>
            <div className="col-12 col-lg-2">
              <label className="form-label fw-semibold">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    end_date: event.target.value,
                  }))
                }
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  </div>
);

// Generic searchable dropdown — fetchFn receives { searchText, limit, offset }.
// resultKey: key inside result where the array lives ("list" or "data").
const PackSelect = ({ fetchFn, labelKey = "name", resultKey = "list", placeholder, value, onChange }) => {
  const [inputText, setInputText] = useState("");
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await fetchFn({
          searchText: inputText || undefined,
          limit: 20,
          offset: 0,
        });
        if (!cancelled) setOptions(response?.result?.[resultKey] || []);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [inputText, fetchFn, resultKey]);

  const handleSelect = (pack) => {
    onChange(pack);
    setInputText(pack[labelKey] || "");
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setInputText("");
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    setIsOpen(true);
    if (!e.target.value) onChange(null);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={inputText}
          onFocus={() => setIsOpen(true)}
          onChange={handleInputChange}
        />
        {value && (
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={handleClear}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            zIndex: 1050,
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            border: "1px solid #dee2e6",
            borderRadius: "0.375rem",
            maxHeight: 220,
            overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {isLoading ? (
            <div className="px-3 py-2 text-muted small">Loading...</div>
          ) : options.length ? (
            options.map((pack) => (
              <div
                key={`pack-opt-${pack.id}`}
                className="px-3 py-2 small"
                style={{
                  cursor: "pointer",
                  borderBottom: "1px solid #f0f0f0",
                  transition: "background 0.1s",
                }}
                onMouseDown={() => handleSelect(pack)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f8f9fa")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "")
                }
              >
                {pack[labelKey] || pack.name || pack.id}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-muted small">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCards = ({ cards, isLoading }) => (
  <div className="row g-3 mb-4">
    {cards.map((card) => (
      <div className="col-12 col-sm-6 col-lg-4" key={card.title}>
        <div
          className="card h-100"
          style={{
            borderRadius: "16px",
            border: "1px solid #e9edf4",
            boxShadow: "0 6px 18px rgba(16, 24, 40, 0.06)",
          }}
        >
          <div className="card-body d-flex align-items-center gap-3 py-4 px-4">
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: card.iconBg,
                color: card.iconColor,
                fontSize: "22px",
                flexShrink: 0,
              }}
            >
              <i className={card.icon} />
            </div>
            <div>
              <div className="text-muted small mb-1">{card.title}</div>
              <div
                className="fw-bold"
                style={{ fontSize: "1.75rem", color: card.iconColor }}
              >
                {isLoading ? "—" : card.value}
              </div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const PurchaseTable = ({
  historyList,
  historyLoading,
  historySearch,
  setHistorySearch,
  columns,
  limit,
  setLimit,
  offset,
  setOffset,
  historyTotal,
}) => {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(historyTotal / limit));
  const canGoPrev = offset > 0;
  const canGoNext = offset + limit < historyTotal;

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h6 className="card-title fw-semibold m-0">Purchase History</h6>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 300 }}
            placeholder="Search by name, email, pack..."
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
          />
        </div>

        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className="text-center fs-7">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    Loading...
                  </td>
                </tr>
              ) : historyList.length ? (
                historyList.map((item, idx) => (
                  <tr key={`ph-${item.id || idx}`}>
                    {columns.map((col) => (
                      <td key={col.key} className="text-center fs-7">
                        {col.render ? col.render(item) : item[col.key] || "-"}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    No purchase history found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-end align-items-center mt-3 gap-2 flex-wrap">
          <div className="d-inline-flex align-items-center gap-2">
            <span className="fs-7">Show</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 90 }}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              {LIMIT_OPTIONS.map((val) => (
                <option key={`limit-${val}`} value={val}>
                  {val}
                </option>
              ))}
            </select>
            <span className="fs-7">per page</span>
          </div>
          <div className="flex-grow-1" />
          <span className="fs-7 text-muted">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-outline-primary btn-sm"
            disabled={!canGoPrev}
            onClick={() => setOffset(offset - limit)}
          >
            Prev
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            disabled={!canGoNext}
            onClick={() => setOffset(offset + limit)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Quest Packs section ──────────────────────────────────────────────────────

const QuestPackSection = () => {
  const [selectedPack, setSelectedPack] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [filters, setFilters] = useState({
    filter: "all",
    day: "",
    start_date: "",
    end_date: "",
  });
  const [historySearch, setHistorySearch] = useState("");
  const [historyList, setHistoryList] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);

  const dateFilterPayload = useMemo(
    () => buildDashboardStyleDateFilterPayload(filters),
    [filters],
  );

  const handleFilterChange = (event) => {
    const nextFilter = event.target.value;
    setFilters((prev) => ({
      filter: nextFilter,
      day: nextFilter === "day" ? prev.day : "",
      start_date: nextFilter === "date_range" ? prev.start_date : "",
      end_date: nextFilter === "date_range" ? prev.end_date : "",
    }));
  };

  useEffect(() => {
    setStatsLoading(true);
    fetchQuestPackPurchaseStatsAPI({
      mstchallengeid: selectedPack?.id || undefined,
      ...dateFilterPayload,
    })
      .then((res) => {
        if (res?.success) setStats(res.result || null);
      })
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, [selectedPack, dateFilterPayload]);

  useEffect(() => {
    setOffset(0);
  }, [historySearch, selectedPack, limit, filters]);

  useEffect(() => {
    setHistoryLoading(true);
    fetchQuestPackPurchaseHistoryAPI({
      mstchallengeid: selectedPack?.id || undefined,
      searchText: historySearch || undefined,
      limit,
      offset,
      ...dateFilterPayload,
    })
      .then((res) => {
        if (res?.success) {
          setHistoryList(res.result?.list || []);
          setHistoryTotal(Number(res.result?.totalCount || 0));
        }
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [selectedPack, historySearch, limit, offset, dateFilterPayload]);

  const statCards = [
    {
      title: "Total Quest Pack Purchases",
      value: formatNumber(stats?.totalQuestPackPurchases),
      icon: "bi bi-bag-check-fill",
      iconBg: "#dff7f4",
      iconColor: "#149b93",
    },
    {
      title: "Total Users Purchased",
      value: formatNumber(stats?.totalUsersPurchased),
      icon: "bi bi-people-fill",
      iconBg: "#e8f0ff",
      iconColor: "#3b5bdb",
    },
    {
      title: "Total Revenue",
      value: formatCurrency(stats?.totalRevenue),
      icon: "bi bi-currency-dollar",
      iconBg: "#f2e8ff",
      iconColor: "#7b4ec8",
    },
  ];

  const columns = [
    { key: "userName", label: "User Name" },
    { key: "userEmail", label: "User Email" },
    { key: "questPackName", label: "Quest Pack Name" },
    {
      key: "amountPaid",
      label: "Amount Paid",
      render: (row) => formatAmount(row.amountPaid),
    },
    {
      key: "purchasedAt",
      label: "Purchased At",
      render: (row) => formatDate(row.purchasedAt),
    },
  ];

  return (
    <>
      <PurchaseFiltersCard
        packLabel="Filter by Quest Pack"
        packSelect={
          <PackSelect
            fetchFn={fetchQuestPackListAPI}
            placeholder="Search quest pack..."
            value={selectedPack}
            onChange={setSelectedPack}
          />
        }
        filters={filters}
        onFilterChange={handleFilterChange}
        setFilters={setFilters}
      />

      <StatCards cards={statCards} isLoading={statsLoading} />

      <PurchaseTable
        historyList={historyList}
        historyLoading={historyLoading}
        historySearch={historySearch}
        setHistorySearch={setHistorySearch}
        columns={columns}
        limit={limit}
        setLimit={setLimit}
        offset={offset}
        setOffset={setOffset}
        historyTotal={historyTotal}
      />
    </>
  );
};

// ── Item Packs section ───────────────────────────────────────────────────────

const ItemPackSection = () => {
  const [selectedPack, setSelectedPack] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [filters, setFilters] = useState({
    filter: "all",
    day: "",
    start_date: "",
    end_date: "",
  });
  const [historySearch, setHistorySearch] = useState("");
  const [historyList, setHistoryList] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);

  const dateFilterPayload = useMemo(
    () => buildDashboardStyleDateFilterPayload(filters),
    [filters],
  );

  const handleFilterChange = (event) => {
    const nextFilter = event.target.value;
    setFilters((prev) => ({
      filter: nextFilter,
      day: nextFilter === "day" ? prev.day : "",
      start_date: nextFilter === "date_range" ? prev.start_date : "",
      end_date: nextFilter === "date_range" ? prev.end_date : "",
    }));
  };

  useEffect(() => {
    setStatsLoading(true);
    fetchItemPackPurchaseStatsAPI({
      itemgroupid: selectedPack?.id || undefined,
      ...dateFilterPayload,
    })
      .then((res) => {
        if (res?.success) setStats(res.result || null);
      })
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, [selectedPack, dateFilterPayload]);

  useEffect(() => {
    setOffset(0);
  }, [historySearch, selectedPack, limit, filters]);

  useEffect(() => {
    setHistoryLoading(true);
    fetchItemPackPurchaseHistoryAPI({
      itemgroupid: selectedPack?.id || undefined,
      searchText: historySearch || undefined,
      limit,
      offset,
      ...dateFilterPayload,
    })
      .then((res) => {
        if (res?.success) {
          setHistoryList(res.result?.list || []);
          setHistoryTotal(Number(res.result?.totalCount || 0));
        }
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [selectedPack, historySearch, limit, offset, dateFilterPayload]);

  const statCards = [
    {
      title: "Total Challenge Pack Purchases",
      value: formatNumber(stats?.totalItemPackPurchases),
      icon: "bi bi-bag-check-fill",
      iconBg: "#dff7f4",
      iconColor: "#149b93",
    },
    {
      title: "Total Users Purchased",
      value: formatNumber(stats?.totalUsersPurchased),
      icon: "bi bi-people-fill",
      iconBg: "#e8f0ff",
      iconColor: "#3b5bdb",
    },
    {
      title: "Total Revenue",
      value: formatCurrency(stats?.totalRevenue),
      icon: "bi bi-currency-dollar",
      iconBg: "#f2e8ff",
      iconColor: "#7b4ec8",
    },
  ];

  const columns = [
    { key: "userName", label: "User Name" },
    { key: "userEmail", label: "User Email" },
    { key: "itemPackName", label: "Challenge Pack Name" },
    {
      key: "amountPaid",
      label: "Amount Paid",
      render: (row) => formatAmount(row.amountPaid),
    },
    {
      key: "purchasedAt",
      label: "Purchased At",
      render: (row) => formatDate(row.purchasedAt),
    },
  ];

  return (
    <>
      <PurchaseFiltersCard
        packLabel="Filter by Challenge Pack"
        packSelect={
          <PackSelect
            fetchFn={fetchItemGroupListAPI}
            labelKey="group_name"
            resultKey="data"
            placeholder="Search challenge pack..."
            value={selectedPack}
            onChange={setSelectedPack}
          />
        }
        filters={filters}
        onFilterChange={handleFilterChange}
        setFilters={setFilters}
      />

      <StatCards cards={statCards} isLoading={statsLoading} />

      <PurchaseTable
        historyList={historyList}
        historyLoading={historyLoading}
        historySearch={historySearch}
        setHistorySearch={setHistorySearch}
        columns={columns}
        limit={limit}
        setLimit={setLimit}
        offset={offset}
        setOffset={setOffset}
        historyTotal={historyTotal}
      />
    </>
  );
};

// ── Hunt Packs section ───────────────────────────────────────────────────────

const HuntPackSection = () => {
  const [selectedPack, setSelectedPack] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [filters, setFilters] = useState({
    filter: "all",
    day: "",
    start_date: "",
    end_date: "",
  });
  const [historySearch, setHistorySearch] = useState("");
  const [historyList, setHistoryList] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);

  const dateFilterPayload = useMemo(
    () => buildDashboardStyleDateFilterPayload(filters),
    [filters],
  );

  const handleFilterChange = (event) => {
    const nextFilter = event.target.value;
    setFilters((prev) => ({
      filter: nextFilter,
      day: nextFilter === "day" ? prev.day : "",
      start_date: nextFilter === "date_range" ? prev.start_date : "",
      end_date: nextFilter === "date_range" ? prev.end_date : "",
    }));
  };

  useEffect(() => {
    setStatsLoading(true);
    fetchHuntPackPurchaseStatsAPI({
      msthuntid: selectedPack?.id || undefined,
      ...dateFilterPayload,
    })
      .then((res) => {
        if (res?.success) setStats(res.result || null);
      })
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, [selectedPack, dateFilterPayload]);

  useEffect(() => {
    setOffset(0);
  }, [historySearch, selectedPack, limit, filters]);

  useEffect(() => {
    setHistoryLoading(true);
    fetchHuntPackPurchaseHistoryAPI({
      msthuntid: selectedPack?.id || undefined,
      searchText: historySearch || undefined,
      limit,
      offset,
      ...dateFilterPayload,
    })
      .then((res) => {
        if (res?.success) {
          setHistoryList(res.result?.list || []);
          setHistoryTotal(Number(res.result?.totalCount || 0));
        }
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [selectedPack, historySearch, limit, offset, dateFilterPayload]);

  const statCards = [
    {
      title: "Total Hunt Pack Purchases",
      value: formatNumber(stats?.totalHuntPackPurchases),
      icon: "bi bi-bag-check-fill",
      iconBg: "#dff7f4",
      iconColor: "#149b93",
    },
    {
      title: "Total Users Purchased",
      value: formatNumber(stats?.totalUsersPurchased),
      icon: "bi bi-people-fill",
      iconBg: "#e8f0ff",
      iconColor: "#3b5bdb",
    },
    {
      title: "Total Revenue",
      value: formatCurrency(stats?.totalRevenue),
      icon: "bi bi-currency-dollar",
      iconBg: "#f2e8ff",
      iconColor: "#7b4ec8",
    },
  ];

  const columns = [
    { key: "userName", label: "User Name" },
    { key: "userEmail", label: "User Email" },
    { key: "huntPackTitle", label: "Hunt Pack Name" },
    {
      key: "amountPaid",
      label: "Amount Paid",
      render: (row) => formatAmount(row.amountPaid),
    },
    {
      key: "purchasedAt",
      label: "Purchased At",
      render: (row) => formatDate(row.purchasedAt),
    },
  ];

  return (
    <>
      <PurchaseFiltersCard
        packLabel="Filter by Hunt Pack"
        packSelect={
          <PackSelect
            fetchFn={fetchHuntPackListAPI}
            labelKey="title"
            placeholder="Search hunt pack..."
            value={selectedPack}
            onChange={setSelectedPack}
          />
        }
        filters={filters}
        onFilterChange={handleFilterChange}
        setFilters={setFilters}
      />

      <StatCards cards={statCards} isLoading={statsLoading} />

      <PurchaseTable
        historyList={historyList}
        historyLoading={historyLoading}
        historySearch={historySearch}
        setHistorySearch={setHistorySearch}
        columns={columns}
        limit={limit}
        setLimit={setLimit}
        offset={offset}
        setOffset={setOffset}
        historyTotal={historyTotal}
      />
    </>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const PurchaseStats = () => {
  const [packType, setPackType] = useState("quest");

  return (
    <main id="main" className="main">
      <Breadcrumb page="Purchase Stats" />

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-lg-4">
              <label className="form-label fw-semibold">Pack Type</label>
              <select
                className="form-select"
                value={packType}
                onChange={(e) => setPackType(e.target.value)}
              >
                {PACK_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {packType === "quest" && <QuestPackSection />}
      {packType === "item" && <ItemPackSection />}
      {packType === "hunt" && <HuntPackSection />}
    </main>
  );
};

export default PurchaseStats;
